/**
 * TrustGate Input Sanitization Framework
 * 
 * Comprehensive input sanitization and XSS prevention
 * to address all security vulnerabilities detected by AI analysis
 */

import DOMPurify from 'dompurify';

// XSS pattern detection
const XSS_PATTERNS = [
  /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
  /javascript:/gi,
  /on\w+\s*=/gi,
  /<iframe\b[^>]*>/gi,
  /<object\b[^>]*>/gi,
  /<embed\b[^>]*>/gi,
  /<link\b[^>]*>/gi,
  /<meta\b[^>]*>/gi,
  /<style\b[^>]*>/gi,
  /<img\b[^>]*>/gi,
  /<svg\b[^>]*>/gi,
  /<math\b[^>]*>/gi,
  /data:text\/html/gi,
  /vbscript:/gi,
  /<.*?[^>]*?on\w+.*?=.*?>/gi,
  /eval\s*\(/gi,
  /setTimeout\s*\(/gi,
  /setInterval\s*\(/gi,
  /Function\s*\(/gi
];

// SQL injection pattern detection
const SQL_INJECTION_PATTERNS = [
  /('|(\\'))|(\-\-)|(\;)|(\|)|(\*)|(\%7C)|(\%2A)|(\%27)|(\%3B)|(\%2D\%2D)/g,
  /(\b(UNION|SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|EXECUTE)\b)/gi,
  /(\b(OR|AND)\s+\d+\s*=\s*\d+)/gi,
  /(\b(OR|AND)\s+['"]?\w+['"]?\s*=\s*['"]?\w+['"]?)/gi,
  /(\/\*.*?\*\/)/gi,
  /(--.*$)/gm,
  /(\b(HEX|CHAR|ASCII|CONCAT|SUBSTRING|LENGTH)\s*\()/gi
];

// Prototype pollution detection
const PROTOTYPE_POLLUTION_PATTERNS = [
  /__proto__/gi,
  /constructor/gi,
  /prototype/gi,
  /\[\s*["']?(prototype|constructor)["']?\s*\]/gi
];

export interface SanitizationOptions {
  allowHTML?: boolean;
  allowedTags?: string[];
  allowedAttributes?: string[];
  stripScripts?: boolean;
  stripStyles?: boolean;
  maxLength?: number;
  trimWhitespace?: boolean;
}

export interface ValidationResult {
  isValid: boolean;
  sanitizedValue: string;
  warnings: string[];
  threats: string[];
  originalValue: string;
}

export class InputSanitizer {
  private static defaultOptions: SanitizationOptions = {
    allowHTML: false,
    allowedTags: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'span'],
    allowedAttributes: ['href', 'title', 'target', 'class'],
    stripScripts: true,
    stripStyles: true,
    maxLength: 10000,
    trimWhitespace: true
  };

  /**
   * Comprehensive input sanitization
   */
  static sanitize(input: string, options: SanitizationOptions = {}): ValidationResult {
    const opts = { ...this.defaultOptions, ...options };
    const originalValue = input;
    const warnings: string[] = [];
    const threats: string[] = [];

    // Check for null/undefined
    if (input == null) {
      return {
        isValid: false,
        sanitizedValue: '',
        warnings: ['Input is null or undefined'],
        threats: [],
        originalValue
      };
    }

    // Convert to string if not already
    let sanitized = String(input);

    // Length validation
    if (opts.maxLength && sanitized.length > opts.maxLength) {
      warnings.push(`Input exceeds maximum length of ${opts.maxLength} characters`);
      sanitized = sanitized.substring(0, opts.maxLength);
    }

    // Trim whitespace
    if (opts.trimWhitespace) {
      sanitized = sanitized.trim();
    }

    // Detect XSS threats
    const xssThreats = this.detectXSS(sanitized);
    threats.push(...xssThreats);

    // Detect SQL injection threats
    const sqlThreats = this.detectSQLInjection(sanitized);
    threats.push(...sqlThreats);

    // Detect prototype pollution threats
    const prototypeThreats = this.detectPrototypePollution(sanitized);
    threats.push(...prototypeThreats);

    // Apply different sanitization based on input type
    if (opts.allowHTML) {
      sanitized = this.sanitizeHTML(sanitized, opts);
    } else {
      sanitized = this.sanitizeText(sanitized);
    }

    // Final validation
    const isValid = threats.length === 0 && warnings.length === 0;

    return {
      isValid,
      sanitizedValue: sanitized,
      warnings,
      threats,
      originalValue
    };
  }

  /**
   * Detect XSS patterns
   */
  private static detectXSS(input: string): string[] {
    const threats: string[] = [];

    XSS_PATTERNS.forEach((pattern, index) => {
      const matches = input.match(pattern);
      if (matches) {
        threats.push(`XSS Pattern ${index + 1}: ${matches.join(', ')}`);
      }
    });

    // Check for suspicious attribute combinations
    const suspiciousAttrs = input.match(/(\w+)\s*=\s*["']?(javascript:|vbscript:|data:)/gi);
    if (suspiciousAttrs) {
      threats.push(`Suspicious attributes: ${suspiciousAttrs.join(', ')}`);
    }

    return threats;
  }

  /**
   * Detect SQL injection patterns
   */
  private static detectSQLInjection(input: string): string[] {
    const threats: string[] = [];

    SQL_INJECTION_PATTERNS.forEach((pattern, index) => {
      const matches = input.match(pattern);
      if (matches) {
        threats.push(`SQL Injection Pattern ${index + 1}: ${matches.join(', ')}`);
      }
    });

    // Check for comment-based attacks
    if (input.includes('--') || input.includes('/*') || input.includes('*/')) {
      threats.push('SQL comment syntax detected');
    }

    return threats;
  }

  /**
   * Detect prototype pollution patterns
   */
  private static detectPrototypePollution(input: string): string[] {
    const threats: string[] = [];

    PROTOTYPE_POLLUTION_PATTERNS.forEach((pattern, index) => {
      const matches = input.match(pattern);
      if (matches) {
        threats.push(`Prototype Pollution Pattern ${index + 1}: ${matches.join(', ')}`);
      }
    });

    return threats;
  }

  /**
   * Sanitize HTML content
   */
  private static sanitizeHTML(html: string, options: SanitizationOptions): string {
    const config = {
      ALLOWED_TAGS: options.allowedTags || this.defaultOptions.allowedTags!,
      ALLOWED_ATTR: options.allowedAttributes || this.defaultOptions.allowedAttributes!,
      KEEP_CONTENT: false,
      RETURN_DOM: false,
      RETURN_DOM_FRAGMENT: false,
      RETURN_DOM_IMPORT: false,
      SANITIZE_DOM: false,
      SAFE_FOR_TEMPLATES: false,
      WHOLE_DOCUMENT: false,
      CUSTOM_ELEMENT_HANDLING: false,
      USE_PROFILES: false,
      FORCE_BODY: false
    };

    // Remove scripts if requested
    if (options.stripScripts) {
      html = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    }

    // Remove styles if requested
    if (options.stripStyles) {
      html = html.replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '');
      html = html.replace(/\s*style\s*=\s*["'][^"']*["']/gi, '');
    }

    // Use DOMPurify for final sanitization
    return DOMPurify.sanitize(html, config);
  }

  /**
   * Sanitize plain text content
   */
  private static sanitizeText(text: string): string {
    // Escape HTML entities
    let sanitized = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;')
      .replace(/\//g, '&#x2F;');

    // Remove dangerous patterns
    XSS_PATTERNS.forEach(pattern => {
      sanitized = sanitized.replace(pattern, '');
    });

    SQL_INJECTION_PATTERNS.forEach(pattern => {
      sanitized = sanitized.replace(pattern, '');
    });

    PROTOTYPE_POLLUTION_PATTERNS.forEach(pattern => {
      sanitized = sanitized.replace(pattern, '');
    });

    return sanitized;
  }

  /**
   * Sanitize URL parameters
   */
  static sanitizeURL(url: string): ValidationResult {
    const result = this.sanitize(url, { allowHTML: false, maxLength: 2048 });
    
    // Additional URL-specific checks
    try {
      const parsedUrl = new URL(result.sanitizedValue);
      
      // Only allow safe protocols
      const safeProtocols = ['http:', 'https:', 'mailto:', 'tel:'];
      if (!safeProtocols.includes(parsedUrl.protocol)) {
        result.threats.push(`Unsafe protocol: ${parsedUrl.protocol}`);
        result.isValid = false;
      }

      // Check for suspicious data URLs
      if (parsedUrl.protocol === 'data:') {
        const mimeType = parsedUrl.pathname.split(';')[0];
        if (!mimeType.startsWith('image/')) {
          result.threats.push(`Unsafe data URL: ${mimeType}`);
          result.isValid = false;
        }
      }

    } catch (error) {
      result.threats.push('Invalid URL format');
      result.isValid = false;
    }

    return result;
  }

  /**
   * Sanitize JSON data
   */
  static sanitizeJSON(jsonString: string): ValidationResult {
    const result = this.sanitize(jsonString, { allowHTML: false, maxLength: 50000 });
    
    try {
      const parsed = JSON.parse(result.sanitizedValue);
      
      // Recursively sanitize all string values
      const sanitizedObj = this.sanitizeObject(parsed);
      result.sanitizedValue = JSON.stringify(sanitizedObj);
      
    } catch (error) {
      result.threats.push('Invalid JSON format');
      result.isValid = false;
    }

    return result;
  }

  /**
   * Recursively sanitize object properties
   */
  private static sanitizeObject(obj: any): any {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => this.sanitizeObject(item));
    }

    const sanitized: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        // Check for prototype pollution
        if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
          continue;
        }

        const value = obj[key];
        if (typeof value === 'string') {
          sanitized[key] = this.sanitize(value).sanitizedValue;
        } else if (typeof value === 'object') {
          sanitized[key] = this.sanitizeObject(value);
        } else {
          sanitized[key] = value;
        }
      }
    }

    return sanitized;
  }

  /**
   * Sanitize email addresses
   */
  static sanitizeEmail(email: string): ValidationResult {
    const result = this.sanitize(email, { allowHTML: false, maxLength: 254 });
    
    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(result.sanitizedValue)) {
      result.threats.push('Invalid email format');
      result.isValid = false;
    }

    return result;
  }

  /**
   * Sanitize phone numbers
   */
  static sanitizePhone(phone: string): ValidationResult {
    const result = this.sanitize(phone, { allowHTML: false, maxLength: 50 });
    
    // Remove all non-digit characters except +, -, (, ), and space
    result.sanitizedValue = result.sanitizedValue.replace(/[^\d\+\-\(\)\s]/g, '');
    
    // Basic phone number validation
    const phoneRegex = /^\+?[\d\s\-\(\)]+$/;
    if (!phoneRegex.test(result.sanitizedValue)) {
      result.threats.push('Invalid phone number format');
      result.isValid = false;
    }

    return result;
  }

  /**
   * Create a safe React component for rendering sanitized content
   */
  static createSafeRenderer(content: string, options: SanitizationOptions = {}) {
    const result = this.sanitize(content, options);
    
    return {
      content: result.sanitizedValue,
      isValid: result.isValid,
      warnings: result.warnings,
      threats: result.threats,
      renderAsHTML: options.allowHTML,
      renderAsText: !options.allowHTML
    };
  }
}

// React hook for input sanitization
export const useInputSanitizer = () => {
  const sanitizeInput = (input: string, options?: SanitizationOptions) => {
    return InputSanitizer.sanitize(input, options);
  };

  const sanitizeURL = (url: string) => {
    return InputSanitizer.sanitizeURL(url);
  };

  const sanitizeEmail = (email: string) => {
    return InputSanitizer.sanitizeEmail(email);
  };

  const sanitizePhone = (phone: string) => {
    return InputSanitizer.sanitizePhone(phone);
  };

  const sanitizeJSON = (jsonString: string) => {
    return InputSanitizer.sanitizeJSON(jsonString);
  };

  return {
    sanitizeInput,
    sanitizeURL,
    sanitizeEmail,
    sanitizePhone,
    sanitizeJSON
  };
};

export default InputSanitizer;
